import {
  Entity,
  type EntityFields,
  type EntityRecord,
  Fields,
} from '@burnsred/entity';
import { default as DjangoRestFramework } from '@burnsred/entity-duck-namespace-drf';

import { duckAppName } from '@/constants';

class UserEntity extends Entity {
  static idField = 'id';

  static paginated = true;

  static paths = {
    apiBase: '/br_auth/v1/user/',
  };

  static fields: EntityFields<UserEntityFields> = {
    id: new Fields.IdField(),
    uuid: new Fields.CharField({ blank: true, local: true }),
    email: new Fields.CharField({ blank: true, local: true }),
    first_name: new Fields.CharField({ blank: true, local: true }),
    last_name: new Fields.CharField({ blank: true, local: true }),
    full_name: new Fields.CharField({ blank: true, local: true }),
  };

  static dataToRecord(data: Partial<UserEntityFields>) {
    return super.dataToRecord(data) as UserEntityRecord;
  }

  static toString(record: UserEntityRecord | null | undefined) {
    return userDisplayName(record);
  }
}

export type UserEntityFields = {
  id: string;
  uuid: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
};

export type UserEntityRecord = EntityRecord<UserEntityFields>;

type UserLike = {
  get(key: 'full_name' | 'email' | 'first_name' | 'last_name'): unknown;
};

export const userDisplayName = (record: UserLike | null | undefined) => {
  if (!record) return '';
  const fullName = String(record.get('full_name') ?? '').trim();
  if (fullName) return fullName;
  const composed = [record.get('first_name'), record.get('last_name')]
    .filter(Boolean)
    .join(' ')
    .trim();
  return composed || String(record.get('email') ?? '');
};

export const userInitials = (record: UserLike | null | undefined) => {
  const name = userDisplayName(record);
  if (!name) return '';
  const source = name.includes('@') ? name.split('@')[0] : name;
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  const initials =
    parts.length > 1
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`
      : source.slice(0, 2);
  return initials.toUpperCase();
};

UserEntity.duck = new DjangoRestFramework({
  app: duckAppName,
  entity: UserEntity,
  name: 'UserEntity',
});

export default UserEntity;
